import { useEffect, useMemo, type ReactNode } from 'react';
import { useUiStore, resolveIsDark } from '@shared/stores/uiStore';
import { semanticLight, semanticDark } from '@shared/design-system/semantic';
import { baseCssVars, semanticToCssVars, buildVarString } from '@shared/design-system/cssVars';

interface ThemeProviderProps {
  children: ReactNode;
}

const STYLE_ID = 'ds-runtime-vars';

export function ThemeProvider({ children }: ThemeProviderProps) {
  const colorScheme = useUiStore((s) => s.colorScheme);
  const setColorScheme = useUiStore((s) => s.setColorScheme);
  const isDark = resolveIsDark(colorScheme);

  const cssText = useMemo(() => {
    const semantic = isDark ? semanticDark : semanticLight;
    const merged = { ...baseCssVars, ...semanticToCssVars(semantic) };
    return `:root { ${buildVarString(merged)} }`;
  }, [isDark]);

  useEffect(() => {
    let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = cssText;
    document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  }, [cssText, isDark]);

  useEffect(() => {
    if (colorScheme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setColorScheme('system');
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, [colorScheme, setColorScheme]);

  return <>{children}</>;
}
